type Subscriber = (isLoading: boolean) => void;

export interface ThemeLoadingControllerOptions {
	/** Safety timeout (ms) after which loading auto-clears so the UI never hangs. */
	timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 15000;

/**
 * Engine-agnostic loading state for theme switches. One instance per map.
 *
 * `begin()` is called from the shared EditorProvider when a theme is selected.
 * `end()` is called by each engine's completion adapter once the theme is
 * actually painted (Esri: sprite-refresh settle; Mapbox: map idle). A safety
 * timeout guarantees the indicator never hangs if a completion signal misfires.
 */
export class ThemeLoadingController {
	private _isLoading = false;
	private _subscribers = new Set<Subscriber>();
	private _timer: ReturnType<typeof setTimeout> | null = null;
	private readonly _timeoutMs: number;

	constructor(options: ThemeLoadingControllerOptions = {}) {
		this._timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
	}

	get isLoading(): boolean {
		return this._isLoading;
	}

	begin(): void {
		this._clearTimer();
		this._timer = setTimeout(() => this.end(), this._timeoutMs);
		if (this._isLoading) return; // timer restarted; state already true
		this._isLoading = true;
		this._notify();
	}

	end(): void {
		this._clearTimer();
		if (!this._isLoading) return; // idempotent
		this._isLoading = false;
		this._notify();
	}

	subscribe(cb: Subscriber): () => void {
		this._subscribers.add(cb);
		return () => this._subscribers.delete(cb);
	}

	private _notify(): void {
		this._subscribers.forEach(cb => cb(this._isLoading));
	}

	private _clearTimer(): void {
		if (this._timer !== null) {
			clearTimeout(this._timer);
			this._timer = null;
		}
	}
}
